import * as React from 'react';

import { useTranslation } from 'react-i18next';
import { AvatarInline, Button, ButtonGroup, Icon, Space, Tooltip, useNotify } from 'ebs-design';
import LogsPanelContext from 'contexts/LogsPanelContext';
import { UserRoleCRM } from 'hooks/useFetcher/interfaces';
import { useRolePermission } from 'hooks/useRolePermission';
import { Call, Play, Trash } from 'resources/icons';
import { dateTimeFormat, format } from 'libs';
import cn from 'classnames';
import { CallCategoryType, CallType, Log, Properties } from 'types';
import { useCallsAPI } from 'api';
import { useMutation, useQueryClient } from 'react-query';
import { AxiosError } from 'axios';
import { OTHER_SOURCE } from 'app-constants';

interface ActivityLogCallProps {
  log: Log;
  onDelete: (id) => void;
}

export const ActivityLogCall: React.FC<ActivityLogCallProps> = ({ log, onDelete }) => {
  const { t } = useTranslation();
  const { callItem, openPlayer } = React.useContext(LogsPanelContext);
  const { postCallsOneC, editCall } = useCallsAPI();
  const notify = useNotify();
  const queryClient = useQueryClient();

  const onSuccessMutate = (): void => {
    queryClient.invalidateQueries(['lead-logs']);
    notify.success({ title: t('success.successDataSave') });
  };

  const { mutate } = useMutation(() => postCallsOneC({ id: log?.call?.id }), {
    onError: (e: AxiosError) => {
      const errorMessage = (e?.response as Properties)?.detail;

      notify.error({ title: errorMessage });
    },
    onSuccess: onSuccessMutate,
  });

  const { mutate: editCallMutate, isLoading } = useMutation(
    (data: { category: CallCategoryType }) =>
      editCall({
        id: log?.call?.id,
        data,
      }),
    {
      onError: (e: AxiosError) => {
        const errorMessage = (e?.response as Properties)?.detail;
        notify.error({ title: errorMessage });
      },
      onSuccess: onSuccessMutate,
    },
  );

  const handleOpenPlayer = (): void => {
    if (log.call) {
      openPlayer(log.call);
    }
  };

  const onClickSaveToOneC = (): void => mutate();

  const noneCallType =
    !log?.call?.category &&
    log?.call?.type === CallType.OUTGOING &&
    log?.call?.source === OTHER_SOURCE;

  const playButton = (
    <Button
      type="ghost"
      size="small"
      className={cn('activity-log-item__delete', { active: callItem?.id === log?.call?.id })}
      disabled={!log.call?.file || noneCallType}
      onClick={handleOpenPlayer}
      prefix={<Icon className="activity-log-item__delete__icon" component={Play} />}
    />
  );

  return (
    <Space justify="space-between">
      <div className="activity-log-item__content">{log.description}</div>
      <Space className="activity-log-item__right-side">
        <Tooltip
          placement="bottom"
          trigger="click"
          bodyClass="no-padding"
          tooltip={
            <>
              <Button
                block
                type="text"
                className="mt-2"
                onClick={() => editCallMutate({ category: CallCategoryType.sales })}
              >
                {t('calls.sales')}
              </Button>

              <Button
                block
                type="text"
                onClick={() => editCallMutate({ category: CallCategoryType.outstanding })}
              >
                {t('calls.outstanding')}
              </Button>
            </>
          }
        >
          <Button
            type="primary"
            className={cn('ebs-button', { 'danger-button': noneCallType })}
            loading={isLoading}
          >
            <Space>
              <Icon component={Call} />

              {noneCallType
                ? t('calls.select_type')
                : t(`calls.${log?.call?.category ? log?.call?.category : 'select_type'}`)}
            </Space>
          </Button>
        </Tooltip>

        <Button type="primary" onClick={onClickSaveToOneC} disabled={noneCallType}>
          {t('calls.saveTo1C')}
        </Button>

        <span className="activity-log-item__created-at">
          {format(log.created_at, dateTimeFormat)}
        </span>
        {log.created_by && (
          <AvatarInline
            type="dynamic"
            shortAlt={[
              (log.created_by?.first_name || '')[0],
              (log.created_by?.last_name || '')[0],
            ].join('')}
            alt={[log.created_by?.first_name, log.created_by?.last_name].join(' ') || '---'}
            circle
          />
        )}

        {useRolePermission([UserRoleCRM.admin]) ? (
          <ButtonGroup>
            {playButton}
            <Button
              type="ghost"
              size="small"
              disabled={noneCallType}
              className="activity-log-item__delete"
              onClick={() => onDelete(log.id)}
              prefix={<Icon className="activity-log-item__delete__icon" component={Trash} />}
            />
          </ButtonGroup>
        ) : (
          playButton
        )}
      </Space>
    </Space>
  );
};
